import { EraQL, Ty } from '@cotera/era';
import type { WorkflowEdge } from './workflow-edge';
import type { WorkflowNode } from './workflow-node-base';
import { WorkflowRunInputSchema } from './workflow-run-input';

export type WorkflowGraphNodeRef = {
  readonly id: string;
  readonly inputShape: string | null;
};

export type WorkflowGraphEdgeRef = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
};

/**
 * Definition graph view used for entry-node / run-input resolution.
 */
export class WorkflowGraph {
  private constructor(
    readonly nodes: readonly WorkflowGraphNodeRef[],
    readonly edges: readonly WorkflowGraphEdgeRef[]
  ) {}

  static create(params: {
    readonly nodes: readonly WorkflowGraphNodeRef[];
    readonly edges: readonly WorkflowGraphEdgeRef[];
  }): WorkflowGraph {
    return new WorkflowGraph(params.nodes, params.edges);
  }

  static fromDomain(
    nodes: readonly WorkflowNode[],
    edges: readonly WorkflowEdge[]
  ): WorkflowGraph {
    return WorkflowGraph.create({
      nodes: nodes.map((node) => ({
        id: node.id,
        inputShape: node.inputShape,
      })),
      edges: edges.map((edge) => ({
        fromNodeId: edge.fromNodeId,
        toNodeId: edge.toNodeId,
      })),
    });
  }

  /** Entry nodes = definition nodes with no incoming edges (matches run seeding). */
  entryNodeIds(): string[] {
    const nodeIds = this.nodes.map((node) => node.id);
    const targets = new Set(this.edges.map((edge) => edge.toNodeId));
    const entries = nodeIds.filter((id) => !targets.has(id));
    if (entries.length > 0) {
      return entries;
    }
    return [...nodeIds];
  }

  /**
   * ERA type for manual workflow run input, derived from entry-node
   * `input_shape` strings.
   */
  inputTy(): Ty.ExtendedAttributeType {
    const shapedEntryNodes = this.shapedEntryInputShapes();

    if (shapedEntryNodes.length === 0) {
      return Ty.structOf({});
    }

    const uniqueShapes = [...new Set(shapedEntryNodes)];
    if (uniqueShapes.length === 1) {
      const parsed = parseInputShape(uniqueShapes[0]!);
      if (parsed === null) {
        return Ty.ty('super');
      }
      return parsed;
    }

    const parsedTypes = uniqueShapes
      .map((shape) => parseInputShape(shape))
      .filter((ty): ty is Ty.ExtendedAttributeType => ty !== null);

    if (parsedTypes.length !== uniqueShapes.length) {
      return Ty.ty('super');
    }

    const merged = Ty.mergeTypes(parsedTypes);
    if (merged === null) {
      return Ty.ty('super');
    }

    return merged;
  }

  inputSchema(): WorkflowRunInputSchema {
    return WorkflowRunInputSchema.fromTy(this.inputTy());
  }

  private shapedEntryInputShapes(): string[] {
    const entryNodeIds = new Set(this.entryNodeIds());
    return this.nodes
      .filter(
        (node) =>
          entryNodeIds.has(node.id) &&
          node.inputShape !== null &&
          node.inputShape.trim() !== ''
      )
      .map((node) => node.inputShape!.trim());
  }
}

function parseInputShape(inputShape: string): Ty.ExtendedAttributeType | null {
  const parsed = EraQL.parseTy(inputShape);
  if (parsed.isErr()) {
    return null;
  }
  return parsed.value;
}
